<section
  class="showcase"
  [class.showcase--playing]="isPlaying"
  [class.showcase--paused]="isPaused"
  [class.showcase--in]="isInView"
  [class.showcase--transition-prepared]="transitionPhase === 'prepared'"
  [class.showcase--sliding]="transitionPhase === 'sliding'"
  role="region"
  [attr.aria-label]="showGalleryHeading && gallery.title ? gallery.title : 'Project image showcase'"
  [style.--showcase-accent]="gallery.lightboxAccentColor || '#a5eb55'"
  (mouseenter)="pauseAutoplay()"
  (mouseleave)="resumeAutoplay()">

  @if (showGalleryHeading || gallery.intro) {
  <div class="showcase__header">
    @if (showGalleryHeading) {
    <div class="showcase__eyebrow">
      <span class="showcase__eyebrow-num">{{ displayOrderLabel }}</span>
      <span class="showcase__eyebrow-sep">—</span>
      <span class="showcase__eyebrow-label">{{ headerLabel || 'Gallery' }}</span>
    </div>
    @if (gallery.title) {
    <h3 class="showcase__header-title">{{ gallery.title }}</h3>
    }
    }
    @if (gallery.intro) {
    <p class="showcase__header-intro">{{ gallery.intro }}</p>
    }
  </div>
  }

  <!-- Slideshow stage -->
  <div
    class="showcase__stage"
    [style.--sc-interval]="(gallery.autoplayInterval ?? 5000) + 'ms'"
    (click)="openAt(activeIndex)"
    role="button"
    tabindex="0"
    [attr.aria-label]="'Open fullscreen — image ' + (activeIndex + 1) + ' of ' + items.length"
    (keydown.enter)="openAt(activeIndex)"
    (keydown.space)="$event.preventDefault(); openAt(activeIndex)">

    @for (item of items; track item._trackKey; let i = $index) {
      <div
        class="showcase__slide"
        [class.showcase__slide--active]="i === activeIndex"
        [class.showcase__slide--outgoing]="i === outgoingIndex"
        [class.showcase__slide--incoming]="i === incomingIndex"
        [attr.aria-hidden]="i !== activeIndex">
        @if (item._isVideo) {
          <video
            class="showcase__slide-media"
            [class.showcase__slide-media--size-standard]="item._displaySize === 'standard'"
            [class.showcase__slide-media--size-wide]="item._displaySize === 'wide'"
            [class.showcase__slide-media--size-tall]="item._displaySize === 'tall'"
            [class.showcase__slide-media--size-feature]="item._displaySize === 'feature'"
            [class.showcase__slide-media--ratio-landscape]="item.aspectRatio === 'landscape'"
            [class.showcase__slide-media--ratio-portrait]="item.aspectRatio === 'portrait'"
            [class.showcase__slide-media--ratio-square]="item.aspectRatio === 'square'"
            [class.showcase__slide-media--ratio-cinematic]="item.aspectRatio === 'cinematic'"
            muted playsinline preload="none"
            [poster]="item._posterUrl"
            [appLazyVideoSource]="item._mediaUrl">
          </video>
        } @else if (item._isText) {
          <article class="showcase__slide-text">
            @if (item.eyebrow) { <span class="showcase__slide-text-eyebrow">{{ item.eyebrow }}</span> }
            @if (item.title) { <h3 class="showcase__slide-text-title">{{ item.title }}</h3> }
            @if (item.body) { <p class="showcase__slide-text-body">{{ item.body }}</p> }
          </article>
        } @else {
          <img
            class="showcase__slide-media"
            [class.showcase__slide-media--size-standard]="item._displaySize === 'standard'"
            [class.showcase__slide-media--size-wide]="item._displaySize === 'wide'"
            [class.showcase__slide-media--size-tall]="item._displaySize === 'tall'"
            [class.showcase__slide-media--size-feature]="item._displaySize === 'feature'"
            [class.showcase__slide-media--ratio-landscape]="item.aspectRatio === 'landscape'"
            [class.showcase__slide-media--ratio-portrait]="item.aspectRatio === 'portrait'"
            [class.showcase__slide-media--ratio-square]="item.aspectRatio === 'square'"
            [class.showcase__slide-media--ratio-cinematic]="item.aspectRatio === 'cinematic'"
            [src]="item._imageSrc"
            [attr.srcset]="item._imageSrcSet || null"
            sizes="(max-width: 640px) calc(100vw - 2rem), (max-width: 1440px) calc(100vw - 4rem), 86rem"
            [alt]="item.alt || item.caption || ('Image ' + (i + 1))"
            loading="eager"
            decoding="async"
          />
        }
        @if (item.caption) {
          <div class="showcase__slide-caption">{{ item.caption }}</div>
        }
      </div>
    }

    <!-- Hover controls (stopPropagation prevents stage click from firing) -->
    <div class="showcase__controls" (click)="$event.stopPropagation()">

      @if (items.length > 1) {
        <button class="showcase__btn showcase__btn--prev" type="button" (click)="prev()" aria-label="Previous image">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
            <polyline points="15 18 9 12 15 6"/>
          </svg>
        </button>
        <button class="showcase__btn showcase__btn--next" type="button" (click)="next()" aria-label="Next image">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
            <polyline points="9 18 15 12 9 6"/>
          </svg>
        </button>
      }

      <button
        class="showcase__btn showcase__btn--expand"
        type="button"
        (click)="openAt(activeIndex)"
        aria-label="Open fullscreen">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round">
          <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3"/>
        </svg>
      </button>

      <div class="showcase__counter" aria-live="polite" aria-atomic="true">
        <span class="showcase__counter-cur">{{ activeIndex + 1 }}</span>
        <span class="showcase__counter-sep">—</span>
        <span class="showcase__counter-total">{{ items.length }}</span>
      </div>

      @if (items.length > 1) {
        <div class="showcase__dots" role="tablist" aria-label="Image navigation">
          @for (item of items; track item._trackKey; let i = $index) {
            <button
              class="showcase__dot"
              [class.showcase__dot--active]="i === activeIndex"
              type="button"
              role="tab"
              [attr.aria-selected]="i === activeIndex"
              [attr.aria-label]="'Go to image ' + (i + 1)"
              (click)="goTo(i)">
            </button>
          }
        </div>
      }

    </div>
  </div>

  <!-- Lightbox -->
  @if (lightbox.isOpen) {
    <app-lightbox
      [items]="items"
      [activeIndex]="activeIndex"
      [showThumbnails]="showLightboxThumbnails"
      [accentColor]="gallery.lightboxAccentColor ?? null"
      [analyticsLayout]="gallery.layout"
      (closeLightbox)="onLightboxClose()"
      (indexChange)="onLightboxIndexChange($event)">
    </app-lightbox>
  }
</section>
